/**
 * Viewport-driven table sizing (docs/UX-FROM-SINGLEDIDGE.md 5.16). Priority:
 * your grid, then the piles and holding spot, then opponent tiles, which
 * shrink first. Pure, so it is unit-tested.
 */
export interface TableLayout {
  /** Your card width (height is 1.5x). */
  cw: number;
  /** Pile card width. */
  pw: number;
  /** Held card width. */
  hw: number;
  /** Opponent tile columns and rows. */
  cols: number;
  rows: number;
  /** Mini cell width (height 1.5x). */
  mini: number;
  /** Width of one opponent tile, in px. */
  tileW: number;
  /** Tile is compact: name and numbers on one line. */
  compact: boolean;
  /** Landscape phone: grid beside the table instead of under it. */
  side: boolean;
  /** Short phone: slimmer spacing. */
  tight: boolean;
  /** Desktop and landscape tablet: piles beside your grid, opponents across the full width. */
  wide: boolean;
  /** Portrait phone (docs/HINTS-LAYOUT-TUTORIAL.md A2): the .phone CSS replaces .tight. */
  phone: boolean;
  /** Phone with 4 or 5 opponents: slim strip tiles (medallion and total beside the mini grid). */
  strip: boolean;
  /** Phone tile height, px (0 elsewhere). */
  tileH: number;
  /** Phone gap between the piles, px (0 elsewhere). */
  pgap: number;
  /** Phone spare height above the piles and above your grid, px (0 elsewhere). */
  gapTop: number;
  gapMid: number;
}

export interface SafeArea {
  top: number;
  bottom: number;
}

/** The new fields at their values on the pre-phone paths. */
const LEGACY = { phone: false, strip: false, tileH: 0, pgap: 0, gapTop: 0, gapMid: 0 } as const;

export const TOP_BAR = 50;
const ME_BAR = 46;
const GAP = 8;
export const ROW_GAP = 10;

function tileGrid(n: number, wide: boolean): { cols: number; rows: number }[] {
  if (n <= 0) return [{ cols: 1, rows: 0 }];
  const opts: { cols: number; rows: number }[] = [];
  if (wide || n <= 3) opts.push({ cols: n, rows: 1 });
  if (n === 4) opts.push({ cols: 2, rows: 2 }, { cols: 4, rows: 1 });
  if (n === 5) opts.push({ cols: 3, rows: 2 }, { cols: 5, rows: 1 });
  if (n === 3) opts.push({ cols: 3, rows: 1 });
  if (n === 2) opts.push({ cols: 2, rows: 1 });
  if (n === 1) opts.push({ cols: 1, rows: 1 });
  return opts;
}

/** Mini cell width that fits a tile of the given size, or 0 if none fits. */
function miniFor(tileW: number, tileH: number, compact: boolean, cap = 44): number {
  const header = compact ? 26 : 36;
  const byH = (tileH - header - 12 - 3) / 2 / 1.5;
  const byW = (tileW - 16 - 9) / 4;
  return Math.floor(Math.min(byH, byW, cap));
}

/** Gap between the piles in the wide layout, and between the piles and your grid. */
export const WIDE_PILE_GAP = 36;
export const WIDE_MID_GAP = 56;
const WIDE_PAD = 24;

/**
 * Wide screens (laptop, desktop, landscape tablet): opponents across the whole
 * width on top, then the piles and your grid side by side, so the grid can use
 * the full height instead of sharing it with the piles.
 */
function wideLayout(W: number, H: number, opponents: number): TableLayout | null {
  const minMini = opponents >= 5 ? 22 : 26;
  for (let cw = 300; cw >= 70; cw -= 2) {
    const pw = Math.round(cw * 0.78);
    const hw = Math.max(100, Math.round(pw * 1.22));
    // grid: two rows + column gap + chips + me bar + paddings
    const mineH = cw * 3 + 6 + 26 + 50 + 16;
    const centerH = hw * 1.5 + 8 + 28 + 16;
    const mainH = Math.max(mineH, centerH);
    const mainW = cw * 4 + 30 + 24 + pw * 2 + hw + WIDE_PILE_GAP * 2 + 24 + WIDE_MID_GAP;
    if (mainW > W - WIDE_PAD * 2) continue;
    const oppH = H - TOP_BAR - mainH - 16;
    const oppW = W - WIDE_PAD * 2;
    let pick: TableLayout | null = null;
    for (const { cols, rows } of tileGrid(opponents, true)) {
      if (rows < 1) continue;
      const tileW = Math.min(440, (oppW - (cols - 1) * 10) / cols);
      const tileH = (oppH - (rows - 1) * ROW_GAP) / rows;
      const mini = miniFor(tileW, tileH, false, 72);
      // Opponents stay readable, roughly a quarter of your card size.
      const want = Math.max(minMini, Math.round(cw * 0.24));
      if (mini >= want && (!pick || mini > pick.mini)) {
        // Hug the mini grid (plus room for the name) so tiles never float half empty.
        const hug = Math.round(Math.max(mini * 4 + 29, 180) * 1.25);
        pick = { cw, pw, hw, cols, rows, mini, tileW: Math.floor(Math.min(tileW, hug)), compact: false, side: false, tight: false, wide: true, ...LEGACY };
      }
    }
    if (pick) return pick;
  }
  return null;
}

/** Phone portrait sizing (docs/HINTS-LAYOUT-TUTORIAL.md A2). Heights match table.css .phone rules. */
export const PHONE = {
  TOP_BAR: 50,
  CENTER_FIXED: 42, // center padding 6 + 2, held lift 6, gap 4, status line 24
  MINE_FIXED: 94, // mine padding 4 + 6, grid pad 6, card gap 4, chip gap 4, chip 20, mebar 4 + 46
  OPP_PAD: 14, // opponents band padding 8 + 6
  ROW_GAP: 8,
  TILE_GAP: 6,
  HW_MAX: 136,
  CW_MIN: 44,
};
/** [mini width, held-card ratio, cw that counts as comfortable] in the order we give things up. */
const PHONE_TIERS: [number, number, number][] = [
  [20, 1.45, 64], [19, 1.45, 64], [18, 1.45, 64], [17, 1.45, 64], [16, 1.45, 64],
  [15, 1.45, 58], [14, 1.45, 56], [14, 1.3, 50], [14, 1.2, 44],
];

/** Height of the opponents band for a phone layout (padding + tile rows). */
export function phoneOppH(l: Pick<TableLayout, 'rows' | 'tileH'>): number {
  return PHONE.OPP_PAD + l.rows * l.tileH + (l.rows - 1) * PHONE.ROW_GAP;
}

function phoneLayout(W: number, H: number, n: number): TableLayout | null {
  const M0 = W < 370 ? 18 : 20;
  const strip = n >= 4;
  const cols = n <= 3 ? n : n === 4 ? 2 : 3;
  const rows = n <= 3 ? 1 : 2;
  const cwCap = Math.min(Math.floor(0.19 * W), Math.floor((W - 54) / 4));
  const pgap = Math.min(28, Math.max(12, Math.round(0.04 * W)));
  const pileHwMax = Math.floor((W - 24 - 2 * pgap) / 2.6); // hw + 2 piles at 0.8 hw
  let res: { M: number; cw: number; avail: number; tileW: number; tileH: number; oppH: number } | null = null;
  for (const [M, ratio, comfy] of PHONE_TIERS) {
    if (M > M0) continue;
    let tileW: number;
    let tileH: number;
    if (strip) {
      tileW = 53 + 4 * M;
      if (cols * tileW + (cols - 1) * PHONE.TILE_GAP + 16 > W) continue;
      tileH = 13 + 3 * M;
    } else {
      tileW = Math.min(172, Math.floor((W - 16 - (n - 1) * PHONE.TILE_GAP) / n));
      if (4 * M + 21 > tileW) continue;
      tileH = 40 + 3 * M;
    }
    const oppH = PHONE.OPP_PAD + rows * tileH + (rows - 1) * PHONE.ROW_GAP;
    const avail = H - PHONE.TOP_BAR - oppH - PHONE.CENTER_FIXED - PHONE.MINE_FIXED;
    let cw: number | null = null;
    for (let c = cwCap; c >= PHONE.CW_MIN; c--) {
      const h = Math.min(PHONE.HW_MAX, Math.round(ratio * c), pileHwMax);
      if (1.5 * h + 3 * c <= avail) {
        cw = c;
        break;
      }
    }
    if (cw === null) continue;
    res = { M, cw, avail, tileW, tileH, oppH };
    if (cw >= comfy || cw === cwCap) break;
  }
  if (!res) return null;
  const { cw } = res;
  const hw = Math.min(PHONE.HW_MAX, pileHwMax, Math.round(1.75 * cw), Math.floor((res.avail - 3 * cw) / 1.5));
  const pw = Math.min(Math.round(0.8 * hw), Math.round(1.15 * cw));
  const slack = Math.max(0, Math.floor(res.avail - 3 * cw - 1.5 * hw));
  return {
    cw, pw, hw, cols, rows, mini: res.M, tileW: res.tileW, tileH: res.tileH,
    compact: true, side: false, tight: false, wide: false, // .phone CSS replaces the .tight rules
    phone: true, strip, pgap, gapTop: Math.floor(slack * 0.25), gapMid: Math.floor(slack * 0.25),
  };
}

export function computeLayout(W: number, H: number, opponents: number, hasSelf = true, safe: SafeArea = { top: 0, bottom: 0 }): TableLayout {
  if (hasSelf && opponents > 0 && W >= 900 && H >= 600 && W >= H * 1.15) {
    const wide = wideLayout(W, H, opponents);
    if (wide) return wide;
  }
  const width = Math.min(W, 1180);
  const side = hasSelf && W > H * 1.25 && H <= 560;
  if (hasSelf && opponents > 0 && W < 700 && H > W && !side) {
    const phone = phoneLayout(W, H - safe.top - safe.bottom, opponents);
    if (phone) return phone;
  }
  return { ...legacyLayout(W, H, opponents, hasSelf, width, side), ...LEGACY };
}

/** The pre-phone sizing loop, unchanged: portrait tablets, landscape phones, spectators, tiny screens. */
function legacyLayout(W: number, H: number, opponents: number, hasSelf: boolean, width: number, side: boolean): Omit<TableLayout, keyof typeof LEGACY> {
  const tight = !side && H < 720 && W < 700;
  // One short status line, no hint line, no pile labels (REDESIGN.md 7.2).
  const STATUS_H = tight ? 24 : 28;
  const CHIPS_H = tight ? 22 : 26;
  const HINT_H = 0;
  const LABELS = 8;
  const minMini = opponents >= 5 ? 14 : opponents >= 4 ? 16 : 18;
  type Legacy = Omit<TableLayout, keyof typeof LEGACY>;
  let best: Legacy | null = null;

  // Phones never reach the old 124px cap (width limits them first); tablets in portrait can go bigger.
  const maxCw = W >= 700 ? 200 : 124;
  const miniCap = W >= 700 && !side ? 60 : 44;
  for (let cw = maxCw; cw >= 50; cw -= 2) {
    const gridW = side ? width * 0.48 : width;
    if (hasSelf && cw * 4 + 86 > gridW) continue;
    const pw = Math.round(Math.max(46, Math.min(cw * 0.85, W >= 700 ? 150 : 100)));
    const hw = Math.round(pw * 1.2);
    const centerH = hw * 1.5 + LABELS + STATUS_H + GAP;
    const myH = hasSelf ? cw * 1.5 * 2 + 6 + CHIPS_H + ME_BAR + HINT_H + GAP : 0;
    const used = side ? Math.max(myH, 0) : centerH + myH;
    let oppH = H - TOP_BAR - used - GAP * 2;
    let oppW = width - 24;
    if (side) {
      oppH = H - TOP_BAR - centerH - GAP * 2;
      oppW = width * 0.5 - 24;
      if (myH > H - TOP_BAR - GAP) continue;
    }
    if (opponents === 0) {
      best = { cw, pw, hw, cols: 1, rows: 0, mini: 0, tileW: 0, compact: false, side, tight, wide: false };
      break;
    }
    let pick: Legacy | null = null;
    for (const { cols, rows } of tileGrid(opponents, width >= 700 || side)) {
      // Tiles are capped at 320px wide (TableScreen's grid template).
      const tileW = Math.min(side ? Infinity : 320, (oppW - (cols - 1) * 6) / cols);
      // Rows sit 10px apart so a tile's status chip never touches the tile below.
      const tileH = (oppH - (rows - 1) * ROW_GAP) / rows;
      const roomy = miniFor(tileW, tileH, false, miniCap);
      const slim = miniFor(tileW, tileH, true, miniCap);
      // Two-line tiles show full names; take them unless they cost a lot of card size.
      const compact = roomy < minMini || roomy < slim * 0.78;
      const mini = compact ? slim : roomy;
      // On roomy screens keep opponents readable instead of maxing your grid.
      const want = width >= 700 && H >= 600 ? Math.max(minMini, Math.min(40, Math.round(cw * 0.34))) : minMini;
      if (mini >= want && (!pick || mini > pick.mini)) {
        pick = { cw, pw, hw, cols, rows, mini, tileW: Math.floor(tileW), compact, side, tight, wide: false };
      }
    }
    if (pick) {
      best = pick;
      break;
    }
  }
  if (!best) {
    // Very small screens: smallest grid, compact tiles.
    const cw = 50;
    const n = Math.max(1, opponents);
    const cols = n <= 3 ? n : 3;
    best = { cw, pw: 46, hw: 55, cols, rows: Math.ceil(n / cols), mini: 12, tileW: Math.floor((W - 24 - (cols - 1) * 6) / cols), compact: true, side, tight, wide: false };
  }
  return best;
}

/** The phone travel art is 4:5. */
const PHONE_ART_ASPECT = 0.8;

/**
 * Where to crop the portrait art so Tibbs stays whole: the object-position x
 * (0..1) that centers his span in a box of the given aspect, and his center in
 * box coordinates, used as the Ken Burns origin so the push never shoves him
 * off an edge. Pure, so it is unit-tested.
 */
export function phoneCrop(tibbs: readonly [number, number], boxAspect: number): { pos: number; origin: number } {
  const v = boxAspect / PHONE_ART_ASPECT; // visible fraction of the art's width
  const c = (tibbs[0] + tibbs[1]) / 2;
  if (v >= 1) return { pos: 0.5, origin: c };
  const pos = Math.min(1, Math.max(0, (c - 0.5 * v) / (1 - v)));
  return { pos, origin: pos + (c - pos) / v };
}
